import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  IconButton,
  Avatar,
  Stack,
  useTheme
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import DashboardSidebar from '../components/DashboardSidebar';
import { useAuth } from '../util/auth';
import { useBanksWithBonuses } from '../util/db';
import { PageContainer, MainContent } from "../styles/SharedStyles";
import Meta from "./../components/Meta";

function BonusAI() {
  const theme = useTheme();
  const messagesEndRef = useRef(null);
  const [message, setMessage] = useState('');
  const [chatHistory, setChatHistory] = useState([
    {
      type: 'bot',
      content: "Hi! I'm your Bonus AI assistant. I can help you maximize your bank bonus earnings. Which bank bonus would you like help with?"
    }
  ]);
  
  const auth = useAuth();
  const { data: banks } = useBanksWithBonuses(auth.user?.uid);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatHistory]);

  const handleSend = async () => {
    if (!message.trim()) return;

    // Add user message
    setChatHistory(prev => [...prev, { type: 'user', content: message }]);

    try {
      const response = await fetch('http://localhost:4006/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message,
          userId: auth.user?.uid,
          bankId: null // Add bankId when discussing specific bank
        }),
      });

      const data = await response.json();
      
      setChatHistory(prev => [...prev, { 
        type: 'bot', 
        content: data.response.text,
        suggestions: data.response.suggestions,
        nextSteps: data.response.nextSteps
      }]);
    } catch (error) {
      console.error('Error:', error);
      setChatHistory(prev => [...prev, { 
        type: 'bot', 
        content: 'Sorry, I encountered an error. Please try again.'
      }]);
    }

    setMessage('');
  };

  console.log('Available banks:', banks?.length);

  return (
    <>
      <Meta title="Bonus AI" />
      <PageContainer>
        <DashboardSidebar />
        <MainContent>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Bonus AI Assistant
          </Typography>
          
          <Paper 
            elevation={0}
            sx={{ 
              height: 'calc(100vh - 200px)',
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: theme.palette.mode === 'dark' ? '#1A1A1A' : '#fff',
              borderRadius: 2
            }}
          >
            {/* Chat messages */}
            <Box sx={{ 
              flex: 1, 
              overflowY: 'auto',
              p: 3,
              display: 'flex',
              flexDirection: 'column',
              gap: 2
            }}>
              {chatHistory.map((msg, index) => (
                <Stack
                  key={index}
                  direction="row"
                  spacing={2}
                  alignSelf={msg.type === 'user' ? 'flex-end' : 'flex-start'}
                  sx={{ maxWidth: '70%' }}
                >
                  {msg.type === 'bot' && (
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      <SmartToyIcon />
                    </Avatar>
                  )}
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      backgroundColor: msg.type === 'user' 
                        ? 'primary.main'
                        : theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.05)' 
                          : 'rgba(0, 0, 0, 0.05)',
                      color: msg.type === 'user' ? 'white' : 'text.primary',
                      borderRadius: 2
                    }}
                  >
                    <Typography>{msg.content}</Typography>
                  </Paper>
                  {msg.type === 'user' && (
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      {auth.user?.photoURL ? (
                        <img src={auth.user.photoURL} alt="User" style={{ width: '100%', height: '100%' }} />
                      ) : (
                        auth.user?.email?.[0].toUpperCase()
                      )}
                    </Avatar>
                  )}
                </Stack>
              ))}
              <div ref={messagesEndRef} />
            </Box>

            {/* Input area */}
            <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
              <Stack direction="row" spacing={2}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Ask about any bank bonus..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                />
                <IconButton 
                  onClick={handleSend}
                  color="primary"
                  sx={{ 
                    bgcolor: 'primary.main',
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'primary.dark'
                    },
                    borderRadius: 2
                  }}
                >
                  <SendIcon />
                </IconButton>
              </Stack>
            </Box>
          </Paper>
        </MainContent>
      </PageContainer>
    </>
  );
}

export default BonusAI; 